import { Observation } from "@medplum/fhirtypes";
import { Smart } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import { WithExtension } from "../../components/resource-documents";
import { getObservationDate, ObservationSeries } from "../../table-transforms/series-utils";
import { VitalRowData } from "./vital-rows";

const SYSTOLIC_BP_CODE = "http://loinc.org: 8480-6";
const DIASTOLIC_BP_CODE = "http://loinc.org: 8462-4";

type BloodPressurePartition = {
  systolic: ObservationSeries | undefined;
  diastolic: ObservationSeries | undefined;
  other: ObservationSeries[];
};

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";

  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return "-";

  return dayjsDate.format(ISO_DATE);
}

export function partitionBloodPressureSeries(series: ObservationSeries[]): BloodPressurePartition {
  const partition: BloodPressurePartition = {
    systolic: undefined,
    diastolic: undefined,
    other: [],
  };

  for (const s of series) {
    if (s.code === SYSTOLIC_BP_CODE) {
      partition.systolic = s;
    } else if (s.code === DIASTOLIC_BP_CODE) {
      partition.diastolic = s;
    } else {
      partition.other.push(s);
    }
  }

  return partition;
}

export function mergeBloodPressureSeries(
  systolic: ObservationSeries,
  diastolic: ObservationSeries
): ObservationSeries {
  const combinedResources = [...systolic.resources, ...diastolic.resources].sort((a, b) => {
    const dateA = dayjs(getObservationDate(a));
    const dateB = dayjs(getObservationDate(b));
    return dateA.diff(dateB);
  });

  const mostRecentDate = dayjs(systolic.latestDate).isAfter(dayjs(diastolic.latestDate))
    ? systolic.latestDate
    : diastolic.latestDate;

  const earliestDate = dayjs(systolic.earliestDate).isBefore(dayjs(diastolic.earliestDate))
    ? systolic.earliestDate
    : diastolic.earliestDate;

  return {
    code: "blood-pressure-combined",
    codeDisplay: "Blood Pressure",
    resourceType: "Observation",
    resources: combinedResources,
    latestDate: mostRecentDate,
    earliestDate,
  };
}

export function createBloodPressureRow(
  systolic: ObservationSeries,
  diastolic: ObservationSeries
): VitalRowData {
  const latestSystolic = systolic.resources[systolic.resources.length - 1];
  const latestDiastolic = diastolic.resources[diastolic.resources.length - 1];

  if (!latestSystolic || !latestDiastolic) {
    throw new Error("Series should always have at least one resource");
  }

  const systolicValue = latestSystolic.valueQuantity?.value ?? "-";
  const diastolicValue = latestDiastolic.valueQuantity?.value ?? "-";
  const unit = latestSystolic.valueQuantity?.unit?.replace(/[{()}]/g, "") ?? "";

  const combinedSeries = mergeBloodPressureSeries(systolic, diastolic);
  const bpData = extractBloodPressureDataForChart(combinedSeries);

  const defaultDisplayValue = `${systolicValue}/${diastolicValue}${unit ? ` ${unit}` : ""}`;

  return {
    id: "series-blood-pressure-combined",
    observation: "Blood Pressure",
    mostRecentValue: defaultDisplayValue,
    mostRecentDate: formatDate(combinedSeries.latestDate),
    originalData: combinedSeries,
    chartSeries: [
      { name: "Systolic", data: bpData.systolic, color: "#3182CE" },
      { name: "Diastolic", data: bpData.diastolic, color: "#E53E3E" },
    ],
    originalValue: undefined,
    originalUnit: unit || undefined,
    unitInfo: { vitalType: "other", originalUnit: unit, normalizedUnit: unit },
    displayValue: () => defaultDisplayValue,
  };
}

function isSystolicObservation(obs: Smart<Observation>): boolean {
  return (
    obs.code?.coding?.some(coding => {
      const system = coding.system || "";
      const code = coding.code || "";
      return `${system}: ${code}` === SYSTOLIC_BP_CODE;
    }) ?? false
  );
}

function isDiastolicObservation(obs: Smart<Observation>): boolean {
  return (
    obs.code?.coding?.some(coding => {
      const system = coding.system || "";
      const code = coding.code || "";
      return `${system}: ${code}` === DIASTOLIC_BP_CODE;
    }) ?? false
  );
}

export function extractBloodPressureDataForChart(series: ObservationSeries): {
  systolic: Array<{ date: string; value: number }>;
  diastolic: Array<{ date: string; value: number }>;
} {
  const systolicData: Array<{ date: string; value: number }> = [];
  const diastolicData: Array<{ date: string; value: number }> = [];

  for (const resource of series.resources) {
    const date = getObservationDate(resource);
    if (!date || date === "-") continue;

    const value = resource.valueQuantity?.value;
    if (value === undefined) continue;

    if (isSystolicObservation(resource)) {
      systolicData.push({ date, value });
    } else if (isDiastolicObservation(resource)) {
      diastolicData.push({ date, value });
    }
  }

  return { systolic: systolicData, diastolic: diastolicData };
}

/**
 * Creates synthetic paired blood pressure resources for the documents table.
 * Pairs systolic and diastolic observations by date/time to show combined BP values.
 */
export function createPairedBloodPressureResources(series: ObservationSeries): WithExtension[] {
  const pairedResources: WithExtension[] = [];
  const processedDates = new Set<string>();

  for (const resource of series.resources) {
    const date = getObservationDate(resource);
    if (!date || date === "-" || processedDates.has(date)) continue;

    const isSystolic = isSystolicObservation(resource);
    const isDiastolic = isDiastolicObservation(resource);

    if (!isSystolic && !isDiastolic) {
      pairedResources.push(resource as WithExtension);
      continue;
    }

    // Find matching pair observation with same date
    const pairObservation = series.resources.find(other => {
      if (other === resource) return false;
      const otherDate = getObservationDate(other);
      if (otherDate !== date) return false;

      if (isSystolic) return isDiastolicObservation(other);
      if (isDiastolic) return isSystolicObservation(other);
      return false;
    });

    if (pairObservation) {
      // Found a pair - create synthetic combined resource
      processedDates.add(date);

      const systolicObs = isSystolic ? resource : pairObservation;
      const diastolicObs = isDiastolic ? resource : pairObservation;

      const systolicValue = systolicObs.valueQuantity?.value ?? null;
      const diastolicValue = diastolicObs.valueQuantity?.value ?? null;
      const unit = systolicObs.valueQuantity?.unit?.replace(/[{()}]/g, "") ?? "";

      const combinedValue =
        systolicValue !== null && diastolicValue !== null
          ? `${systolicValue}/${diastolicValue}${unit ? ` ${unit}` : ""}`
          : systolicValue !== null
          ? `${systolicValue}/?${unit ? ` ${unit}` : ""}`
          : diastolicValue !== null
          ? `?/${diastolicValue}${unit ? ` ${unit}` : ""}`
          : "-";

      pairedResources.push({
        resourceType: "Observation",
        effectiveDateTime: systolicObs.effectiveDateTime || diastolicObs.effectiveDateTime,
        effectivePeriod: systolicObs.effectivePeriod || diastolicObs.effectivePeriod,
        issued: systolicObs.issued || diastolicObs.issued,
        valueString: combinedValue,
        extension: systolicObs.extension || diastolicObs.extension,
      } as WithExtension);
    } else {
      // No pair found - show individual reading with ? for missing value
      const value = resource.valueQuantity?.value ?? null;
      const unit = resource.valueQuantity?.unit?.replace(/[{()}]/g, "") ?? "";

      const displayValue =
        value !== null
          ? isSystolic
            ? `${value}/?${unit ? ` ${unit}` : ""}`
            : `?/${value}${unit ? ` ${unit}` : ""}`
          : "-";

      pairedResources.push({
        resourceType: "Observation",
        effectiveDateTime: resource.effectiveDateTime,
        effectivePeriod: resource.effectivePeriod,
        issued: resource.issued,
        valueString: displayValue,
        extension: resource.extension,
      } as WithExtension);
    }
  }

  return pairedResources;
}
